import React, { useState, useEffect, useCallback } from 'react';
import { Plus, X, Wallet, RefreshCw, Coins, Bitcoin, Building2 } from 'lucide-react';
import { apiFetch } from '../utils/api.ts';
import Swal from 'sweetalert2'; // Importamos Swal


interface FormularioActivosProps {
  idUsuario: number;
  onActualizar: () => void; 
}

const FormularioActivos: React.FC<FormularioActivosProps> = ({ idUsuario, onActualizar }) => {
  const [showForm, setShowForm] = useState(false);
  const [loadingActivos, setLoadingActivos] = useState(false);
  const [guardando, setGuardando] = useState(false);
  const [opcionesActivos, setOpcionesActivos] = useState<any[]>([]);
  const [tipoSeleccionado, setTipoSeleccionado] = useState<'crypto' | 'cedears' | ''>('');
  
  const [formData, setFormData] = useState({
    nombreActivo: '',
    descripcionActivo: '',
    cantidad: '',
    tipoActivo: ''
  });

  // 1. CARGA PROACTIVA: Cargamos los activos apenas el componente existe
  // Esto hace que cuando el usuario toque "Registrar", la lista ya esté lista.
  const fetchActivosDisponibles = useCallback(async () => {
    if (opcionesActivos.length > 0) return; // Si ya hay datos, no volvemos a pedir
    
    setLoadingActivos(true);
    try {
      const response = await apiFetch('/crypto');
      if (response.ok) {
        const data = await response.json();
        setOpcionesActivos(data);
        console.log("Activos cargados proactivamente");
      }
    } catch (error) {
      console.error("Error cargando activos:", error);
    } finally {
      setLoadingActivos(false);
    }
  }, [opcionesActivos.length]);

  useEffect(() => {
    fetchActivosDisponibles(); // Carga al montar
  }, [fetchActivosDisponibles]);

  const handleTipoChange = (tipo: 'crypto' | 'cedears') => {
    setTipoSeleccionado(tipo);
    setFormData(prev => ({ ...prev, nombreActivo: '', tipoActivo: tipo }));
  };

  const handleActivoChange = (nombreSeleccionado: string) => {
    const activoInfo = opcionesActivos.find(a => a.nombre === nombreSeleccionado);
    if (activoInfo) {
      setFormData(prev => ({
        ...prev,
        nombreActivo: activoInfo.nombre,
        tipoActivo: activoInfo.tipo,
        descripcionActivo: activoInfo.tipo === 'crypto' 
          ? `Criptomoneda ${activoInfo.nombre}` 
          : `CEDEAR ${activoInfo.nombre}`
      }));
    }
  };

  const handleSubmit = async () => {
    if (!formData.nombreActivo || !formData.cantidad) {
      return Swal.fire('Atención', 'Por favor completa todos los campos', 'info');
    }
    
    setGuardando(true);
    try {
      const response = await apiFetch('/activos', {
        method: 'POST',
        body: JSON.stringify({
          nombreActivo: formData.nombreActivo,
          descripcionActivo: formData.descripcionActivo,
          cantidad: parseFloat(formData.cantidad),
          tipoActivo: formData.tipoActivo,
          id_usuario: idUsuario 
        })
      });

      if (response.ok) {
        // 2. ACTUALIZACIÓN RÁPIDA: Llamamos al refresco antes de cerrar el form
        onActualizar(); 

        await Swal.fire({
          title: '¡Éxito!',
          text: `${formData.nombreActivo} registrado correctamente`,
          icon: 'success',
          timer: 1500,
          showConfirmButton: false,
          timerProgressBar: true
        });

        setShowForm(false);
        setTipoSeleccionado('');
        setFormData({ nombreActivo: '', descripcionActivo: '', cantidad: '', tipoActivo: '' });
      } else {
        const errorData = await response.json();
        Swal.fire('Error', errorData.error || 'No se pudo guardar', 'error');
      }
    } catch (error) {
      console.error("Error de red:", error);
      Swal.fire('Error', 'Fallo de conexión con el servidor', 'error');
    } finally {
      setGuardando(false);
    }
  };

  const activosFiltrados = opcionesActivos.filter(a => a.tipo === tipoSeleccionado);
  return (
    <div className="space-y-4">
      <button
        onClick={() => setShowForm(!showForm)}
        className={`w-full py-4 rounded-xl shadow-md font-bold text-lg flex items-center justify-center gap-2 transition-all ${
          showForm ? 'bg-gray-100 text-gray-700 border border-gray-300' : 'bg-blue-900 text-white hover:bg-green-700'
        }`}
      >
        {showForm ? <X size={24} /> : <Coins size={24} />}
        {showForm ? 'Cancelar Registro' : 'Nuevo Activo'}
      </button>

      {showForm && (
        <div className="bg-white rounded-xl shadow-lg p-6 border border-indigo-100 animate-in fade-in zoom-in duration-300">
            <h1 className="text-2xl md:text-xl font-bold text-center text-gray-600 tracking-wide border-b-2 border-gray-300 pb-2 mb-4 drop-shadow-[0_2px_2px_rgba(0,0,0,0.15)] transition-colors duration-500 text-gray-100">
              <Wallet className="text-indigo-900" size={22} strokeWidth={2.5} />
              REGISTRAR ACTIVO EN BILLETERA
            </h1>

          <div className="space-y-5">
            <div>
              <label className="text-sm font-semibold text-gray-700 block mb-2">¿Qué tipo de activo es?</label>
              <div className="grid grid-cols-2 gap-3">
                <button
                  type="button"
                  onClick={() => handleTipoChange('crypto')}
                  className={`flex items-center justify-center gap-2 py-3 rounded-lg border-2 transition-all ${
                    tipoSeleccionado === 'crypto' 
                    ? 'border-orange-500 bg-orange-50 text-orange-700' 
                    : 'border-gray-100 bg-gray-50 text-gray-500 hover:border-orange-200'
                  }`}
                >
                  <Bitcoin size={20} /> Cripto
                </button>
                <button
                  type="button"
                  onClick={() => handleTipoChange('cedears')}
                  className={`flex items-center justify-center gap-2 py-3 rounded-lg border-2 transition-all ${
                    tipoSeleccionado === 'cedears' 
                    ? 'border-blue-500 bg-blue-50 text-blue-700' 
                    : 'border-gray-100 bg-gray-50 text-gray-500 hover:border-blue-200'
                  }`}
                >
                  <Building2 size={20} /> CEDEAR
                </button>
              </div>
            </div>

            {tipoSeleccionado && (
              <div className="animate-in slide-in-from-top-2 duration-300">
                <label className="text-sm font-semibold text-gray-700 block mb-1">
                  Selecciona {tipoSeleccionado === 'crypto' ? 'Cripto' : 'Empresa'}
                </label>
                <select
                  value={formData.nombreActivo}
                  onChange={(e) => handleActivoChange(e.target.value)}
                  className="w-full px-4 py-3 bg-gray-50 border border-gray-200 rounded-lg outline-none focus:ring-2 focus:ring-indigo-500 text-gray-900 font-bold"
                >
                  <option value="">{loadingActivos ? 'Cargando...' : '-- Elige una opción --'}</option>
                  {activosFiltrados.map(a => (
                    <option key={a.nombre} value={a.nombre}>
                      {a.nombre}
                    </option>
                  ))}
                </select>
              </div>
            )}

            {formData.nombreActivo && (
              <div className="animate-in slide-in-from-top-2 duration-300">
                <label className="text-sm font-semibold text-gray-700 block mb-1">Cantidad de {formData.nombreActivo}</label>
                <input
                  type="number"
                  step="any"
                  placeholder="0.00"
                  value={formData.cantidad}
                  onChange={(e) => setFormData(prev => ({...prev, cantidad: e.target.value}))}
                  className="w-full px-4 py-3 bg-gray-50 border border-gray-200 rounded-lg outline-none focus:ring-2 focus:ring-indigo-500 font-mono text-gray-900 text-xl"
                />
              </div>
            )}

            <button
              onClick={handleSubmit}
              disabled={guardando || !formData.nombreActivo || !formData.cantidad}
              className="w-full bg-indigo-600 hover:bg-indigo-700 text-white font-bold py-4 rounded-xl shadow-lg flex justify-center items-center gap-2 transition-transform active:scale-95 disabled:opacity-50"
            >
              {guardando ? <RefreshCw className="animate-spin" size={20} /> : 'Confirmar Registro'}
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default FormularioActivos;